export const L10nTemplates = /** @type {const} */ {
  noteTemplates: 'Note Templates',
  emailTemplatesTitle: 'Email Templates',
  changeTemplate: 'Change Template',
  currentTemplate: 'Current template',
  /** Why the meeting's template was chosen (icon tooltip next to template name). */
  appliedBySource: {
    user: 'Template manually set by the user',
    system: 'Template set by the system',
    system_playbook: 'Template applied by a playbook',
    system_purpose: 'Template applied by meeting purpose',
    system_user: 'Template set by the user default',
    system_org: 'Template set by the organization default'
  },
  changeTemplateDisabledWhileApplying:
    'Cannot change template while applying template to meeting',
  changeTemplateDisabledZenMode: 'Cannot change template in Zen Mode',
  myTemplates: 'My templates',
  organizationTemplates: 'Organization templates',
  avomaTemplates: 'Avoma templates',
  createANewTemplate: 'New Template',
  createNewCollection: 'New Collection',
  searchPlaceholder: 'Search for a template..',
  expandSidebar: 'Expand sidebar',
  currentlyInUse: 'Currently in use',
  renameTemplateName: 'Update title',
  renameTemplateDescription: 'Update description',
  edit: 'Edit',
  applyTemplate: 'Apply template',
  previewNotes: 'Preview notes',
  previewTemplate: 'Preview template',
  templatePreview: 'Template Preview',
  createdBy: 'Created by:',
  lastModified: 'Last modified',
  assignedTo: 'Assigned to:',
  noPurposesAssigned: 'No purposes assigned',
  defaultTemplateSwitchTitle: 'Set as my default',
  organizationDefaultTemplateSwitchTitle: "Set as my organization's default",
  promptTitle: 'Prompt',
  notesFormatTitle: 'Notes format:',
  crmMappingTitle: 'CRM Mapping',
  backToTemplate: 'Back to template',
  selectDifferentMeeting: 'Select a different meeting',
  emptyTemplateMessageWithAuthority:
    'No Smart Topics have been added to this template. Please edit to add Smart Topics.',
  emptyTemplateMessageWithoutAuthority: 'Notes template is empty.',
  noAccessMessage:
    'You do not have permission to access this private template.',
  notAvailable: 'N/A',
  readOnly: 'Read-only',
  readOnlyTooltip:
    'This template can be duplicated or used for meetings but cannot be edited or deleted',
  participants: 'Participants',
  participantsDescription: 'List of participants from the conversation.',
  aiDetectedTopics: 'AI Detected Topics',
  aiDetectedTopicsDescription:
    'Avoma AI will generate notes for all the relevant topics detected in the conversation.',
  purposeNotAllowedTooltipForAvomaDefault:
    'Purposes not allowed for Avoma Default template',
  generatingNotes: 'Generating notes...',
  applyTemplateSuccess: 'Template applied and notes generated successfully',
  associatedTemplateSuccess: 'Template applied successfully',
  applyTemplateError: 'Failed to apply template to meeting',
  generateNotesError: 'Failed to generate notes for this meeting',
  errorGeneratingNotesMessage:
    'There was an error generating notes for this meeting. Please try again.',
  noPromptPlaceholder:
    'No prompt added. Enhance this topic by providing a prompt.',
  regenerationHistoryTooltip: 'View notes regeneration history',
  searchTopicPlaceholder: 'Search for a topic',
  createNewTopic: '+ Create New Topic',
  noCategoriesFound: 'No Categories found matching search criteria',
  outdatedTemplate:
    'This template has been updated. Regenerate notes to apply these changes.',
  regenerateNotes: 'Regenerate notes',
  regeneratingNotes: 'Regenerating notes...',
  templateNotFound: 'Template not found',
  personalTemplate: 'My Personal Default',
  organizationDefaultTemplate: 'Organization Default',

  // Confirm modal strings
  setConfirmText: 'Yes, Proceed',
  setPersonalDefaultTitle: 'as your personal default template?',
  setOrgDefaultTitle: "as your organization's default template?",
  setOrgDefaultDescription:
    'Please note that default template set by the meeting owners will take precedence over this organization template',
  assignPurposeConfirmText: 'Assign Purpose',
  assignPurposeTitle: 'template?',
  assignPurposeDescription:
    'This template will be automatically applied to all meetings with the selected purpose(s), taking priority over your personal and organization default templates.',
  selectCollection: 'Select a collection',
  createTemplate: 'Create Template',
  extractAdditionalTopicsTitle: 'Choose how to extract notes',
  extractAdditionalTopicsDisabled:
    'Only topics that are defined in the template',
  extractAdditionalTopicsEnabled: 'For all relevant topics',
  extractAdditionalTopicsConfirmModalTitle: 'Choose how to extract notes',
  extractAdditionalTopicsConfirmModalText: () => (
    <div className='flex flex-col gap-y-2'>
      <span>
        When enabled, future meetings using this template will{' '}
        <strong>
          only capture notes for the specific topics you've predefined
        </strong>
        .
      </span>
      <span>
        Topics not included in your template will be{' '}
        <strong>ignored during note extraction</strong>.
      </span>
      <span>Do you wish to proceed?</span>
    </div>
  ),
  templatePurposesUpdatedSuccessfully: 'Template purposes updated successfully',
  defaultTemplateBehavior: 'Default template behavior',
  organizeUnderSpecificCollection: 'Organize under a specific collection',
  autoApplyTemplateToMeetingsWithSelectedPurposesFor: 'Auto-apply for meetings',
  autoApplyForSelectedPurposesDescription:
    'Automatically use this template for meetings tagged with the selected purpose(s).',
  createCollectionError: 'Failed to create collection',
  updateCollectionError: 'Failed to update collection',
  templateCollectionUpdateError: 'Failed to update template collection',
  deleteDisabledTooltip:
    'Only the template owner and an Avoma admin can delete this template',

  // Template list view sections
  allTemplates: {
    title: 'All Templates',
    getStarted: 'Get Started',
    recentlyAddedTemplates: 'Recently Added Templates',
    forInterview: 'For Interview',
    forSalesDiscoveryAndDemo: 'For Sales',
    forMarketing: 'For Marketing',
    forProduct: 'For Product',
    forRecruiting: 'For Recruiting',
    forOpsAndFinance: 'For Ops and Finance',
    forCsm: 'For CSM',
    forInternalMeetings: 'For Internal Meetings',
    popularTemplates: 'Popular Templates',
    collectionsByTeam: 'Collections By Team'
  },
  methodologyControlled: {
    autoApplied: 'Auto-applied by AI Methodology',
    methodology: 'Methodology',
    description: (methodologyName: string) =>
      `This template is automatically applied to meetings for deals in ${methodologyName}-enabled pipelines`,
    viewSettings: 'View settings'
  },
  filters: {
    collection: 'Collection',
    filterByCollection: 'Filter by Collection'
  },
  emailTemplates: {
    allTemplates: 'All templates',
    myTemplates: 'My templates',
    sharedTemplates: 'Organization templates',
    defaultTemplates: 'Default templates',
    collections: 'Collections',
    title: 'Email Templates',
    pageHeading: 'All Templates',
    pageDescription:
      'Create and manage reusable AI-powered email templates for follow-ups and sequences.',
    searchPlaceholder: 'Search for a template',
    newTemplate: 'New Template',
    createTemplate: 'Create Template',
    create: 'Create',
    collectionCreateSuccess: 'Collection created successfully',
    saveTemplate: 'Save Template',
    createTemplateTitle: 'Create a new template',
    editTemplateTitle: 'Edit template',
    viewTemplateTitle: 'View template',
    name: 'Name',
    prompt: 'Prompt',
    settings: 'Settings',
    templateCount: (count: number) =>
      `${count} template${count === 1 ? '' : 's'}`,
    templateAccessTitle: 'Who can use this template?',
    promptAccessPrivate: 'Only me',
    promptAccessOrganization: 'Anyone in my organization',
    selectPurposePlaceholder: 'Select a purpose',
    settingsDescription:
      'Use variables to personalize emails and keep reusable prompting patterns in one place.',
    availableVariables: 'Available variables',
    howItWorks: 'How it works',
    howItWorksDescription:
      'Use double brackets to tell AI what to fill in. Saved templates can be reused anywhere email templates are supported.',
    createdByYou: 'You',
    createdByAvoma: 'Avoma',
    clearSearchToBrowse: 'Clear your search to browse all templates.',
    createFirstTemplate: 'Create your first email template to get started.',
    noTemplateAdded: 'No template added',
    createSuccess: 'Template created successfully',
    duplicateSuccess: 'Template duplicated successfully',
    updateSuccess: 'Template updated successfully',
    deleteSuccess: 'Template deleted successfully',
    saveError: 'Failed to save template',
    deleteError: 'Failed to delete template',
    duplicateError: 'Failed to duplicate template',
    useTemplate: 'Use',
    templateActions: 'Template actions',
    duplicateTemplate: 'Duplicate',
    deleteCollectionTitle: 'Delete Collection?',
    createCollectionTitle: 'Create a new collection',
    collectionNameRequired: 'Collection name is required',
    deleteCollectionAdditionalText:
      'Templates inside this collection will not be deleted and will continue to exist without the collection.',
    deleteCollectionConfirmText: 'Delete',
    deleteCollectionCancelText: 'Cancel',
    readOnlyDescription:
      'This template is read-only. Duplicate it to create an editable version.',
    lastModifiedOn: (date: string) => `Last modified on ${date}`,
    lastModifiedByOn: (name: string, date: string) =>
      `Last modified by ${name} on ${date}`,
    setAsMyDefault: 'Set as my default',
    setAsMyDefaultSuccess: 'Template set as your default',
    setAsMyDefaultError: 'Failed to set template as default',
    orgDefaultBadge: 'Org default',
    myDefaultBadge: 'My default'
  }
};
